<template>
  <div class="child-comment-container">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading">加载评论中...</div>
    
    <!-- 子评论列表 -->
    <div v-else-if="childComments && childComments.length > 0" class="child-comments-wrapper">
      <div 
        v-for="comment in childComments" 
        :key="comment.id" 
        class="child-comment-item"
      >
        <img :src="comment.userAvatar" :alt="comment.name" class="comment-avatar" />
        <div class="comment-content">
          <div class="comment-header">
            <strong>{{ comment.name }}</strong>
            <span class="comment-time">{{ formatDate(comment.createTime) }}</span>
          </div>
          <p>{{ comment.content }}</p>
          <div class="comment-actions">
            <button 
              @click="toggleCommentLike(comment)"
              :class="['comment-like-btn', { liked: comment.isLiked === 1 }]">
              <i :class="comment.isLiked === 1 ? 'icon-child-comment-liked' : 'icon-child-comment-like'"></i> {{ comment.likeCount }}
            </button>
            
            <!-- 删除评论按钮 - 仅当评论是当前用户发布时显示 -->
            <button 
              v-if="currentUser.id === comment.userId"
              @click.stop="deleteCurrentComment(comment)"
              class="comment-delete-btn"
            >
              <i class="icon-delete"></i> 删除
            </button>
            
            <!-- 举报评论按钮 - 仅当评论不是当前用户发布时显示 -->
            <button 
              v-if="currentUser.id !== comment.userId"
              class="comment-report-btn"
              @click="openCommentReportDialog(comment.id)"
            >
              <i class="icon-report">举报</i>
            </button>
          </div>
        </div>
      </div>
      
      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          layout="prev, pager, next"
          @current-change="handlePageChange"
          :hide-on-single-page="false"
          :pager-count="5"
        />
      </div>
    </div>
    
    <!-- 无子评论时的提示 -->
    <div v-else class="no-child-comments">暂无回复</div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, computed } from 'vue';
import { getChildCommentList, ChildCommentListParams, ChildCommentListResponse, deleteComment as deleteCommentAPI } from '@/user/api/post';
import { likeComment } from '@/user/api/interaction'; // 从interaction API模块导入
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import type { Comment } from '@/user/api/post';
import ReportModal from '@/views/user/report/ReportModal.vue';

export default defineComponent({
  name: 'ChildCommentList',
  props: {
    postId: {
      type: Number,
      required: true,
      default: 0
    },
    parentId: {
      type: Number,
      required: true,
      default: 0
    }
  },
  emits: ['refresh-comments', 'report-comment'], // 添加report-comment事件
  setup(props, { emit }) {
    // 响应式数据
    const childComments = ref<Comment[]>([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const total = ref(0);
    const pageSize = ref(10); // 默认每页10条
    const store = useStore();
    const reportModalRef = ref(); // 添加举报弹窗引用

    // 获取当前登录用户信息
    const currentUser = computed(() => {
      return store.state.userInfo || { id: null };
    });

    // 判断是否已点赞的辅助函数
    const isCommentLiked = (isLikedValue: boolean | null | 0 | 1): boolean => {
      return isLikedValue === true || isLikedValue === 1;
    };

    // 获取子评论列表
    const loadChildComments = async (page: number) => {
      if (props.parentId <= 0) {
        console.warn('parentId 无效，无法加载子评论');
        return;
      }

      try {
        loading.value = true;
        
        // 设置请求参数（子评论分页page从1开始）
        const params: ChildCommentListParams = {
          page: page, // 子评论分页从1开始
          pageSize: pageSize.value,
          postId: props.postId,
          parentId: props.parentId
        };

        console.log('正在发送请求到 /user/comment/childlist，参数:', params);

        // 发送请求获取子评论列表
        const response = await getChildCommentList(params);
        
        console.log('收到子评论列表响应:', response);
        
        // 处理响应数据
        if (response && response.code === 1 && response.data) {
          childComments.value = response.data.records || [];
          total.value = response.data.total || 0;
        } else {
          console.error('获取子评论列表失败:', response?.msg || '未知错误');
          childComments.value = [];
          total.value = 0;
        }
      } catch (error) {
        console.error('获取子评论列表异常:', error);
        childComments.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    // 点赞/取消点赞评论
    const toggleCommentLike = async (comment: Comment) => {
      // 保存当前状态用于异常回滚
      const previousIsLiked = comment.isLiked;
      const previousLikeCount = comment.likeCount;
      
      try {
        // 乐观更新：先更新UI状态
        // 处理 isLiked 为 0|1|boolean|null 的情况
        const wasLiked = isCommentLiked(previousIsLiked);
        comment.isLiked = wasLiked ? 0 : 1;
        comment.likeCount = wasLiked ? previousLikeCount - 1 : previousLikeCount + 1;

        // 调用API进行点赞/取消点赞评论
        const response = await likeComment(comment.id);

        if (response && response.code === 1) {
          // API调用成功，保持乐观更新的状态
          // 从服务器获取最新状态后显示消息
          const isCurrentlyLiked = isCommentLiked(comment.isLiked);
          ElMessage.success(isCurrentlyLiked ? '已点赞' : '已取消点赞');
        } else {
          // API调用失败，回滚UI状态
          comment.isLiked = previousIsLiked;
          comment.likeCount = previousLikeCount;
          
          console.error('点赞/取消点赞失败:', response?.msg || '未知错误');
          ElMessage.error(response?.msg || '操作失败');
        }
      } catch (error: any) {
        // 发生异常时回滚状态
        comment.isLiked = previousIsLiked;
        comment.likeCount = previousLikeCount;
        
        console.error('点赞/取消点赞时发生异常:', error);
        ElMessage.error('网络错误，请稍后再试');
      }
    };

    // 监听页码变化
    watch(currentPage, async (newPage) => {
      await loadChildComments(newPage);
    });

    // 组件挂载时加载第一页数据
    onMounted(async () => {
      await loadChildComments(currentPage.value);
    });

    // 删除评论
    const deleteCurrentComment = async (comment: Comment) => {
      try {
        await ElMessageBox.confirm(
          '确定要删除这个评论吗？此操作不可撤销。',
          '删除确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          }
        );
        
        // 调用删除评论的API
        const response = await deleteCommentAPI(comment.id);
        
        if (response && response.code === 1) {
          ElMessage.success('评论删除成功');
          // 重新加载子评论列表
          await loadChildComments(currentPage.value);
          // 触发刷新父级评论的事件
          emit('refresh-comments');
        } else {
          ElMessage.error(response.msg || '删除评论失败');
        }
      } catch (error) {
        // 用户取消删除或发生错误
        if (error !== 'cancel') {
          console.error('删除评论失败:', error);
          ElMessage.error('删除评论失败');
        }
      }
    };

    // 分页处理
    const handlePageChange = async (page: number) => {
      currentPage.value = page;
      await loadChildComments(page);
    };

    // 举报评论
    const openCommentReportDialog = (commentId: number) => {
      // 这里需要通过某种方式调用举报功能
      // 由于子评论组件中不能直接访问ReportModal
      // 我们通过事件通知父组件来处理举报
      emit('report-comment', { targetType: 2, targetId: commentId });
    };

    return {
      childComments,
      loading,
      currentPage,
      total,
      pageSize,
      currentUser,
      reportModalRef, // 添加举报弹窗引用
      formatDate: (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
      toggleCommentLike,
      deleteCurrentComment,
      handlePageChange,
      openCommentReportDialog
    };
  }
});
</script>

<style scoped>
.child-comment-container {
  padding: 15px;
  margin-top: 10px;
  background-color: #fafafa;
  border-radius: 8px;
}

.loading {
  text-align: center;
  padding: 20px;
  color: #8b6d4b;
  font-size: 16px;
}

.child-comments-wrapper {
  position: relative;
}

.child-comment-item {
  display: flex;
  margin-bottom: 15px;
  padding: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.comment-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 12px;
  object-fit: cover;
}

.comment-content {
  flex: 1;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.comment-time {
  font-size: 0.8em;
  color: #999;
}

.comment-content p {
  margin: 0 0 10px 0;
  line-height: 1.5;
  color: #333;
}

.comment-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comment-like-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #8b6d4b;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 15px;
  transition: all 0.3s;
  min-height: 24px;
  min-width: 24px;
  justify-content: center;
}

.comment-like-btn:hover {
  background-color: #f0e6d4;
  color: #d48341;
}

.comment-like-btn.liked {
  color: #e74c3c;
}

.comment-delete-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #e74c3c;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 15px;
  transition: all 0.3s;
  min-height: 24px;
  min-width: 24px;
  justify-content: center;
}

.comment-delete-btn:hover {
  background-color: #ffebee;
  color: #c0392b;
}

.comment-report-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #ff6b6b;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 15px;
  transition: all 0.3s;
  min-height: 24px;
  min-width: 24px;
  justify-content: center;
}

.comment-report-btn:hover {
  background-color: #ffebee;
  color: #ff5252;
}

/* 图标样式 */
.icon-child-comment-like::before {
  content: "👍";
  font-size: 12px;
}

.icon-child-comment-liked::before {
  content: "❤️";
  font-size: 12px;
}

.icon-delete::before {
  content: "🗑️";
  font-size: 12px;
}

.icon-report::before {
  content: "⚠️";
  font-size: 12px;
}

.thumb-up-icon {
  margin-right: 2px;
  font-size: 14px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

:deep(.el-pagination) {
  --el-pagination-font-size: 14px;
  --el-pagination-button-width: 30px;
  --el-pagination-button-height: 30px;
}

:deep(.el-pager) {
  margin: 0 4px;
}

:deep(.el-pager li) {
  padding: 0 4px;
  margin: 0 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  text-align: center;
  box-sizing: border-box;
}

.no-child-comments {
  text-align: center;
  padding: 20px;
  color: #a8977a;
  font-style: italic;
}
</style>